import type { BuildContext, FeatureOutput, WorldFeature } from '../types';
import { buildNearDetailLod } from '../mesh/nearDetailLod';
import { createDensifyConfig } from '../terrain/densify';
import { createStreetRelief } from '../../site/streetRelief';
import { createGroundElevation } from '../../site/groundElevation';
import { WORLD } from '../../core/config';
import { damDeckFootprints } from '../../site/damDeck';
import { cloisterFloorFootprints } from '../../site/cloisterFloor';
import { fromRing } from '../../geo/clip';
import { drawPaving } from './pavingMesh';
import { placeQuayGoods, QUAY_GOODS } from './quayGoods';
import { drawQuayGoods, goodsObstacles } from './quayGoodsMesh';
import { banksOf } from './banks';
import { marketStockFeature } from './marketStockFeature';
import { stadhuisBannersFeature } from './stadhuisBanners';
import {
  buildCappingBeams,
  quayPostSet,
  quayPostTemplate,
  type QuayTimberKind,
} from './quayTimbers';

// The detail programme's near tier (PLAN, 2026-09-27): posts are drawn within 60 m of the camera.
const QUAY_POSTS = { name: 'quay-posts', radius: 60 } as const;

const quayTimbersFeature: WorldFeature = {
  id: 'streetscape:quayTimbers',
  label: 'Quay timbers',
  weight: 1,
  build(ctx: BuildContext): FeatureOutput {
    const banks = banksOf(ctx.site.waterEdges);
    const decks = ctx.site.bridges.flatMap((bridge) => bridge.deck);
    const { pieces } = buildCappingBeams(
      banks,
      decks,
      ctx.palette,
      ctx.chunks,
      createGroundElevation(ctx.layout)
    );
    const posts = quayPostSet(banks, decks, createGroundElevation(ctx.layout));
    const lod = buildNearDetailLod<QuayTimberKind>(
      [posts],
      new Map([['quayPost', quayPostTemplate(ctx.palette)]]),
      ctx.materials.painted({ instanced: true }),
      QUAY_POSTS
    );
    return {
      objects: [lod.root],
      obstacles: [],
      animated: [],
      counts: { quayBeams: pieces, quayPosts: posts.count },
    };
  },
};

/* The pieces buildWorld paved (streetscape plan section 5), refined for the painter view as the
   ground is, so the two keep their shared outlines. */
const pavingFeature: WorldFeature = {
  id: 'streetscape:paving',
  label: 'Paving',
  weight: 2,
  batches(ctx: BuildContext) {
    const densify = {
      ...createDensifyConfig(ctx.painterView, WORLD.groundY),
      triangulations: ctx.terrainPlans?.paving,
    };
    const pieces = ctx.paving.pieces;
    const relief = createStreetRelief(ctx.layout, { damDeck: false, cloisterFloor: false });
    const decks = [
      ...damDeckFootprints(ctx.layout.landmarks),
      ...cloisterFloorFootprints(ctx.layout),
    ].map(fromRing);
    const size = 96;
    return Array.from({ length: Math.ceil(pieces.length / size) }, (_, i) => ({
      label: `Paving batch ${i + 1}/${Math.ceil(pieces.length / size)}`,
      build: (): FeatureOutput => {
        const drawn = drawPaving(
          pieces.slice(i * size, (i + 1) * size),
          ctx.palette,
          ctx.chunks,
          densify,
          relief,
          decks
        );
        return {
          objects: [],
          obstacles: [],
          animated: [],
          counts: { pavingPieces: drawn.pieces, pavingTriangles: drawn.triangles },
        };
      },
    }));
  },
  build(ctx: BuildContext): FeatureOutput {
    const densify = createDensifyConfig(ctx.painterView, WORLD.groundY);
    const drawn = drawPaving(
      ctx.paving.pieces,
      ctx.palette,
      ctx.chunks,
      densify,
      createStreetRelief(ctx.layout, { damDeck: false, cloisterFloor: false }),
      [...damDeckFootprints(ctx.layout.landmarks), ...cloisterFloorFootprints(ctx.layout)].map(
        fromRing
      )
    );
    return {
      objects: [],
      obstacles: [],
      animated: [],
      counts: { pavingPieces: drawn.pieces, pavingTriangles: drawn.triangles },
    };
  },
};

/* The goods the 1544 woodcut draws on the Oudezijds Voorburgwal quays, on the strip the Keur of
   1501 left open for timber, and on the IJ quay (plan section 6.3). */
const quayGoodsFeature: WorldFeature = {
  id: 'streetscape:quayGoods',
  label: 'Quay goods',
  weight: 1,
  build(ctx: BuildContext): FeatureOutput {
    const placed = placeQuayGoods(QUAY_GOODS, ctx.layout.streets, (p) =>
      ctx.site.queries.isWater(p)
    );
    const drawn = drawQuayGoods(placed, ctx.palette, ctx.chunks, createGroundElevation(ctx.layout));
    return {
      objects: [],
      obstacles: goodsObstacles(placed),
      animated: [],
      counts: { quayGoods: drawn.items, quayGoodsTriangles: drawn.triangles },
    };
  },
};

export const STREETSCAPE_FEATURES: readonly WorldFeature[] = [
  quayTimbersFeature,
  pavingFeature,
  quayGoodsFeature,
  marketStockFeature,
  stadhuisBannersFeature,
];
